import {Component, computed, EventEmitter, Input, OnDestroy, Output, signal} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {KnowledgeStorageService} from '../../shared/service/knowledge-storage/knowledge-storage.service';
import {TranslatePipe} from '@ngx-translate/core';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {Subscription} from 'rxjs';


type LoadingState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; content: SafeHtml }
  | { status: 'error'; message: string };

@Component({
  selector: 'myupdates-html-viewer',
  imports: [MatProgressSpinner, MatIcon, TranslatePipe],
  templateUrl: './html-viewer.component.html',
  styleUrl: './html-viewer.component.scss'
})
export class HtmlViewerComponent implements OnDestroy {

  private contendSubscription?: Subscription;

  private _documentId!: string;
  @Input({required: true})
  set documentId(id: string) {
    // Nur laden, wenn sich die ID ändert
    if (id && id !== this._documentId) {
      this._documentId = id;
      this.loadDynamicContent();
    }
  }

  get documentId(): string {
    return this._documentId;
  }

  /**
   * Konfiguriert, ob im Error-State eine Fehleranzeige angezeigt werden soll.
   * Standard: true (Fehleranzeige wird angezeigt)
   */
  @Input() showErrorDisplay: boolean = true;

  /**
   * Event wird ausgelöst, wenn die Komponente in einen Error-State wechselt.
   * Parent-Komponenten können auf dieses Event hören und entsprechend reagieren.
   */
  @Output() onError = new EventEmitter<{ message: string; error?: any }>();

  successContent = computed(() => {
    const s = this.state();
    return s.status === 'success' ? s.content : '';
  });

  state = signal<LoadingState>({status: 'idle'});

  constructor(
    private contentService: KnowledgeStorageService,
    private readonly sanitizer: DomSanitizer
  ) { }

  ngOnDestroy(): void {
    this.contendSubscription?.unsubscribe();
  }

  private loadDynamicContent(): void {
    this.contendSubscription?.unsubscribe();

    // Die Überprüfung der Id ist redundant wegen { required: true } ... aber schadet nicht
    if (!this.documentId) {
      const errorMessage = 'Keine Document-ID angegeben.';
      this.state.set({
        status: 'error',
        message: errorMessage
      });
      // Error-Event an Parent-Komponente weiterleiten
      this.onError.emit({ message: errorMessage });
      return;
    }

    this.state.set({ status: 'loading' });

    this.contendSubscription = this.contentService.getContent(this.documentId).subscribe({
      next: (content) => {
        const combinedHtml = this.combineHtmlAndCss(content);
        this.state.set({
          status: 'success',
          content: combinedHtml
        });
      },
      error: (error) => {
        const errorMessage = 'Inhalt konnte nicht geladen werden.';
        this.state.set({
          status: 'error',
          message: errorMessage
        });
        // Error-Event an Parent-Komponente weiterleiten
        this.onError.emit({ message: errorMessage, error });
      }
    });
  }

  private combineHtmlAndCss(content: { html: string; css: string }): SafeHtml {
    const combined = `<style>${content.css}</style>${content.html}`;
    return this.sanitizer.bypassSecurityTrustHtml(combined);
  }

}
